import { useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

import { apiPaths } from '../../../service/apiPaths';
import ApiService from '../../../service/ApiService';
import {
    formatDate, formatDateToISODateString, formatTime, limit, Roles, updateComponent, ViewType
} from '../../../util/constant';
import { ILeave } from './Leave.props';
import { getEmployee } from '../Employee/Employee.api';

interface ILeaveView {
  _id: string;
  name: string;
  description: string;
  status: string;
  totalHours: number;
  users: Array<{
    name: string;
    email: string;
    role: string;
    totalHours: number;
    dateWise: Array<{
      date: string;
      hours: number;
    }>;
  }>;
}

const useLeave = () => {
  const [selectedLeaveId, setSelectedLeaveId] = useState<string | null>(null);
  const [selectedLeaveView, setSelectedLeaveView] = useState<ILeaveView | null>(
    null
  );
  const [leaveData, setLeaveData] = useState<ILeave[]>([]);
  const [showConfirmation, setShowConfirmation] = useState<boolean>(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [search, setSearch] = useState<string>("");
  const [page, setPage] = useState<number>(1);
  const [totalPage, setTotalPage] = useState<number>(1);
  const [loading, setLoading] = useState<boolean>(false);
  const [years, setYears] = useState<string[]>([]);
  const [selectedYear, setSelectedYear] = useState<string>("");
  const [selectedMonth, setSelectedMonth] = useState<string>("");
  const [userLeave, setUserLeave] = useState<any>();
  const [user, setUser] = useState<any[]>([]);
  const [userId, setUserId] = useState<string | null>(null);
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState<boolean>(false);
  const [selectedStatus, setSelectedStatus] = useState<string>("");
  const [selectedId, setSelectedId] = useState<any>();
  const [description, setDescription] = useState<string>("");
  const [showAddYearConfirmation, setShowAddYearConfirmation] =
    useState<boolean>(false);
  const Role = localStorage.getItem("role");
  const [showDownloadModal, setShowDownloadModal] = useState<boolean>(false);
  const [downloadYear, setDownloadYear] = useState<string>("");
  const [downloadUserId, setDownloadUserId] = useState<string | null>(null);

  useEffect(() => {
    if (selectedYear && selectedMonth) {
      handleDateFilter(null, null);
    }
  }, [selectedYear, selectedMonth, userId]);
  useEffect(() => {
    if (selectedYear && userId) {
      fetchUserLeave(userId);
    }
  }, [selectedYear, userId]);
  const fetchData = async () => {
    setLoading(true);
    try {
      const result = await ApiService({
        method: "GET",
        endpoint: `${apiPaths.getEmployees}`,
      });
      setUser([{ id: null, name: "select User" }, ...result]);
      setUserId(null);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };
  const fetchUserLeave = async (userId: string) => {
    try {
      let endpoint = ``;
      if (Role === Roles.hr) {
        endpoint = `${apiPaths.getLeaveUser}/${selectedYear}?userId=${userId}&role=${Role}`;
      } else {
        endpoint = `${apiPaths.getLeaveUser}/${selectedYear}?userId=${userId}`;
      }
      const result = await ApiService({
        method: "GET",
        endpoint: endpoint,
      });
      setUserLeave(result);
    } catch (error) {
      console.error("Error fetching user leave:", error);
    }
  };
  const fetchYears = async () => {
    try {
      let endpoint = ``;
      if (Role === Roles.admin) {
        endpoint = `${apiPaths.getYears}?userId=${userId}`;
      } else {
        endpoint = `${apiPaths.getYears}?userId=${userId}&role=${Role}`;
      }
      const result = await ApiService({
        method: "GET",
        endpoint: endpoint,
      });

      const yearsList = Array.isArray(result) ? result : [];
      setYears(yearsList);

      if (yearsList.length > 0) {
        const currentDate = new Date();
        const currentMonth = currentDate.getMonth() + 1;
        const currentYear = currentDate.getFullYear();
        const workingYear =
          currentMonth >= 11
            ? `${currentYear}-${currentYear + 1}`
            : `${currentYear - 1}-${currentYear}`;

        const defaultYear =
          yearsList.find((year) => year === workingYear) ||
          yearsList[yearsList.length - 1];
        setSelectedYear(defaultYear);
      }
    } catch (error) {
      console.error("Error fetching years:", error);
    }
  };

  useEffect(() => {
    fetchYears();
    fetchData();
    const currentMonth = (new Date().getMonth() + 1)
      .toString()
      .padStart(2, "0");
    setSelectedMonth(currentMonth);
  }, []);

  const handleDateFilter = async (
    fromDate: Date | null,
    toDate: Date | null
  ) => {
    setLoading(true);
    try {
      let endpoint = ``;

      if (selectedYear && selectedMonth) {
        const [startYear, endYear] = selectedYear.split("-");

        const yearForMonth =
          parseInt(selectedMonth) >= 11 ? startYear : endYear;
        const yearMonth = `${yearForMonth}-${selectedMonth}`;

        endpoint = `${apiPaths.getLeave}?year=${selectedYear}&month=${yearMonth}&page=${page}&limit=${limit}`;

        if (fromDate && toDate) {
          const formattedFromDate = formatDateToISODateString(fromDate);
          const formattedToDate = formatDateToISODateString(toDate);
          endpoint += `&fromDate=${formattedFromDate}&toDate=${formattedToDate}`;
        }

        if (userId) {
          endpoint += `&userId=${userId}`;
        }
      } else {
        endpoint = `${apiPaths.getLeave}?page=${page}&limit=${limit}`;
        if (search) {
          endpoint += `&search=${search}`;
        }
        if (fromDate && toDate) {
          const formattedFromDate = formatDateToISODateString(fromDate);
          const formattedToDate = formatDateToISODateString(toDate);
          endpoint += `&fromDate=${formattedFromDate}&toDate=${formattedToDate}`;
        }
      }

      const result = await ApiService({
        method: "GET",
        endpoint: endpoint,
      });
      setLeaveData(result?.leaves);
      setTotalPage(result.totalPages);
    } catch (error) {
      console.error("Error fetching filtered data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUp = async (id: string) => {
    setDeletingId(id);
    setShowConfirmation(true);
  };
  const handleCancelStatusUp = () => {
    setShowConfirmation(false);
    setDeletingId(null);
  };

  const handleAdd = () => {
    navigate("/addLeave");
  };
  const updateScreen = (type: ViewType) => {
    updateComponent(type, setSelectedLeaveView, setSelectedLeaveId);
  };
  const handleSelectStatus = (e: any, id: any) => {
    const status = e.target.value;
    if (status === "reject" || status === "approve") {
      setShowModal(true);
      setSelectedStatus(status);
      setSelectedId(id);
    } else {
      handleConfirmStatusUp(id, status, "");
    }
  };


  const handleConfirmStatusUp = async (
    id: any,
    status: any,
    description: string
  ) => {
    setLoading(true);
    
    const updateData: any = {
      status: status,
      comments: description,
    };
    
    // Get Slack channel from already loaded employee data
    const employeeSlackChannel = id.userId?.slackChannelId;

    
    
    if (employeeSlackChannel) {
      updateData.channel = employeeSlackChannel;
      console.log(`Sending Slack notification to: ${employeeSlackChannel}`);
    } else {
      console.warn("No Slack channel configured for this employee");
    }
    
    try {
      await ApiService({
        method: "PUT",
        endpoint: `${apiPaths.updateLeaveRequest}${id._id}/status`,
        data: updateData,
      });
      
      const message = employeeSlackChannel 
        ? "Leave status updated and Slack notification sent"
        : "Leave status updated successfully";
      toast.success(message);
      
      if (selectedMonth && selectedYear) {
        handleDateFilter(null, null);
      }
      if (selectedYear && userId && selectedMonth) {
        fetchUserLeave(userId);
      }
    } catch (error: any) {
      console.error("Error updating leave status:", error);
      toast.error(
        error?.response?.data?.message || "Error updating leave status"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleModalSubmit = () => {
    handleConfirmStatusUp(selectedId, selectedStatus, description);
    setShowModal(false);
    setDescription("");
  };

  const handleModalClose = () => {
    setShowModal(false);
    setDescription("");
  };
  const handleAddNewYear = async () => {
    try {
      await ApiService({
        method: "POST",
        endpoint: `${apiPaths.addYear}`,
      });
      toast.success("New year added successfully");
      fetchYears();
    } catch (error) {
      console.error("Error adding new year:", error);
      toast.error("Error adding new year");
    }
  };

  const handleAddNewYearClick = () => {
    setShowAddYearConfirmation(true);
  };

  const handleAddNewYearConfirm = () => {
    handleAddNewYear();
    setShowAddYearConfirmation(false);
  };

  const handleAddNewYearCancel = () => {
    setShowAddYearConfirmation(false);
  };

  const handleDownloadCSV = async () => {
    try {
      let endpoint = `${apiPaths.getLeave}?year=${downloadYear}`;
      if (downloadUserId) {
        endpoint += `&userId=${downloadUserId}`;
      }
      const response = await ApiService({
        method: "GET",
        endpoint: endpoint,
      });

      // Define headers as per the table
      const headers = [
        "USER",
        "LEAVE TYPE",
        "SUB TYPE",
        "DESCRIPTION",
        "PAID DAYS",
        "UNPAID DAYS",
        "TOTAL DAYS",
        "FROM",
        "TO",
        "REQUESTED DATE",
        "REQUESTED TIME",
        "STATUS",
        "COMMENT",
      ];

      // Map leaves to flat rows
      const rows = (response.leaves || []).map((leave: any) => [
        leave.userId?.name || "",
        leave.type || "",
        leave.subtype || "",
        leave.description || "",
        leave.paidDays ?? "",
        leave.unpaidDays ?? "",
        leave.totalDays ?? "",
        formatDate(leave.fromDate),
        formatDate(leave.toDate),
        formatDate(leave.createdAt),
        formatTime(leave.createdAt),
        leave.status || "",
        leave.comments || "",
      ]);

      // Convert to CSV string
      const csvContent = [
        headers.join(","),
        ...rows.map((row: string[]) =>
          row
            .map((val: string) => `"${String(val).replace(/"/g, '""')}"`)
            .join(",")
        ),
      ].join("\n");

      // Download as CSV
      const blob = new Blob([csvContent], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `leave_data_${downloadYear}${
        downloadUserId ? "_user" : "_all"
      }.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      setShowDownloadModal(false);
      toast.success("CSV downloaded successfully");
    } catch (error) {
      console.error("Error downloading CSV:", error);
      toast.error("Error downloading CSV");
    }
  };

  const handleOpenDownloadModal = () => {
    setShowDownloadModal(true);
    setDownloadYear(selectedYear);
    setDownloadUserId(null);
  };

  const handleCloseDownloadModal = () => {
    setShowDownloadModal(false);
    setDownloadYear("");
    setDownloadUserId(null);
  };

  return {
    selectedLeaveId,
    selectedLeaveView,
    leaveData,
    showConfirmation,
    handleAdd,
    handleStatusUp,
    handleCancelStatusUp,
    handleConfirmStatusUp,
    handleDateFilter,
    updateScreen,
    loading,
    setSearch,
    search,
    setPage,
    page,
    totalPage,
    years,
    selectedYear,
    setSelectedYear,
    selectedMonth,
    setSelectedMonth,
    userLeave,
    user,
    userId,
    setUserId,
    handleSelectStatus,
    showModal,
    handleModalClose,
    handleModalSubmit,
    description,
    setDescription,
    handleAddNewYear,
    handleAddNewYearClick,
    handleAddNewYearConfirm,
    handleAddNewYearCancel,
    showAddYearConfirmation,
    showDownloadModal,
    downloadYear,
    setDownloadYear,
    downloadUserId,
    setDownloadUserId,
    handleDownloadCSV,
    handleOpenDownloadModal,
    handleCloseDownloadModal,
  };
};

export default useLeave;
